/**
 * FormEmailModal
 *
 * Compose-and-send email modal for a form (recipients, template, PDF/XLSX
 * attachments). When no `formId`/`formNumber` props are passed it derives them
 * from the page form via `useFormStore`; the props are the section-level
 * override path (SplitShareControls).
 *
 * Renders an always-open antd `<Modal>`. Callers gate mount/unmount with
 * `{open && <FormEmailModal … />}` so the modal's subscriptions only run while
 * it is shown.
 */

import { FC, useState, useContext, useMemo, useCallback } from 'react';
import type { RcFile } from 'antd/es/upload/interface';
import FormExportModal from '../../pdfExport/components/FormExportModal';
import { Modal, Form, Input, Checkbox, Select, Button, Segmented, Typography, theme } from 'antd';
import { sendEmail } from '../../../services/sendEmail';
import FileLinksSelector, { ShareableFile } from '../../selectors/FileLinksSelector';
import RecipientSelect from '../../selectors/RecipientSelect';
import { useCurrentArtifact } from '../../../use/useCurrentArtifact';
import { useAttachments } from '../../../use/attachments/useAttachments';
import { useFormStore } from '../../../store';
import { UserContext } from '../../../providers/UserContext';
import { useContactSearch } from '../../../use/useContactSearch';
import { useOrganizationParameters } from '../../../use/useOrganizationParameters';
import { MessageContext } from '../../../providers/MessageContext';
import { createEmailLogEntry } from './create/createCommunicationLogEntry';
import { emailAttachmentBlobToFile } from './create/emailAttachmentBlobToFile';
import { artifactAttachmentsFromUploadResults } from './create/artifactAttachmentsFromUploadResults';
import { prepareEmailBody } from './prepareEmailBody';
import { useEmailTemplateContext } from './useEmailTemplateContext';
import { useEmailTemplateResolution } from './useEmailTemplateResolution';
import { useFileSharing } from './use/useFileSharing';
import { usePdfAttachment } from './use/usePdfAttachment';
import { useXlsxAttachment } from './use/useXlsxAttachment';
import { CommunicationLogBundles } from '../../rendering/fields/CommunicationLog/services/CommunicationLogBundles';
import FlexBox from '../../atoms/FlexBox';
import FlexCol from '../../atoms/FlexCol';
import { MAX_LENGTHS, defaultSystemFromEmail, type ReplyToChoice } from '#types/System.Parameters.types';
import { replyToChoicesFor } from '#types';
import { EMAIL_REGEX, EMAIL_MAX_LENGTH } from '#types/System.FieldTypes.types';
import type { VisibilityTypeId } from '#types/System.types';

const { TextArea } = Input;
const { Text } = Typography;

type TreeLikeNode = { entityRecordId?: string; children?: TreeLikeNode[] };

const collectTreeNodeIds = (nodes: TreeLikeNode[], ids: Set<string>) => {
  for (const node of nodes) {
    if (node.entityRecordId) ids.add(node.entityRecordId);
    if (node.children) collectTreeNodeIds(node.children, ids);
  }
};

interface FormEmailModalProps {
  onCancel: () => void;
  /** Override form ID (for section-level email via SplitShareControls) */
  formId?: string;
  /** Override form title (for section-level email via SplitShareControls) */
  formTitle?: string;
  /** Override form number (for section-level email via SplitShareControls) */
  formNumber?: string;
  /** Pre-fill recipient (test convenience) */
  initialTo?: string[];
}

const emailValidator = (_: unknown, value: string[]) => {
  const invalid = (value ?? []).filter((e) => {
    const trimmed = e.trim();
    return trimmed.length > EMAIL_MAX_LENGTH || !EMAIL_REGEX.test(trimmed);
  });
  return invalid.length > 0
    ? Promise.reject(new Error(`Invalid email: ${invalid.join(', ')}`))
    : Promise.resolve();
};

const FormEmailModal: FC<FormEmailModalProps> = ({
  onCancel,
  formId: formIdProp,
  formTitle: formTitleProp,
  formNumber: formNumberProp,
  initialTo,
}) => {
  const [form] = Form.useForm();
  const { token } = theme.useToken();
  const { messageApi } = useContext(MessageContext);
  const [loading, setLoading] = useState(false);
  const [selectedFileLinks, setSelectedFileLinks] = useState<ShareableFile[]>([]);
  const [isPdfPreviewOpen, setIsPdfPreviewOpen] = useState(false);
  const [contactScope, setContactScope] = useState<'form' | 'all'>('form');

  const { form: pageForm, formType, formVersion, splitQuery } = useFormStore();
  const { user } = useContext(UserContext);
  const { data: orgData, getVisibilityTypesForFormType } = useOrganizationParameters();
  const artifact = useCurrentArtifact();
  const data = artifact.kind === 'none' ? null : artifact.data;

  const emailSenderPolicy = orgData?.emailSenderPolicy;
  const [emailSenderModeOverride, setEmailSenderModeOverride] = useState<ReplyToChoice | undefined>(undefined);
  const { functionalAccounts, choice: emailSenderMode } = replyToChoicesFor(
    emailSenderPolicy,
    user?.uid,
    emailSenderModeOverride,
  );
  const replyToOptions = [
    { label: `System email (${defaultSystemFromEmail})`, value: 'system' },
    { label: `My email (${user?.email})`, value: 'user' },
    ...functionalAccounts.map((account) => ({ label: `${account.label} (${account.email})`, value: account.email })),
  ];

  const formId = formIdProp ?? pageForm?.data?.docId ?? '';
  const formNumber = formNumberProp ?? pageForm?.data?.formNumber;
  const formTitle = formTitleProp ?? pageForm?.data?.formNumber ?? 'Form';
  const formVersionNumber = formVersion?.data?.formVersionNumber;
  const formLabel = formNumber && formVersionNumber !== undefined
    ? `${formNumber}-v${formVersionNumber}`
    : formNumber;
  const formAttachments = data?.attachments ?? {};

  const formTypeId = formType?.data?.docId;
  const pdfExportOptions = useMemo(() => {
    const visibilityTypes = getVisibilityTypesForFormType(formTypeId);
    return visibilityTypes.filter((option) => option.isPdfExport === true || option.id === 'pdfview');
  }, [formTypeId, getVisibilityTypesForFormType]);
  const defaultPdfExportMode: VisibilityTypeId = useMemo(
    () => pdfExportOptions[0]?.id ?? 'pdfview',
    [pdfExportOptions]
  );
  const pdfVisibilitySelectOptions = useMemo(
    () =>
      pdfExportOptions.map((option) => ({
        value: option.id,
        label: option.shortLabel ?? option.id,
      })),
    [pdfExportOptions]
  );

  const templateContext = useEmailTemplateContext(formNumber);
  const {
    entries: templateEntries,
    initialTemplate,
    initialSubject,
    initialMessage,
    initialSignature,
    selectOptions: templateSelectOptions,
    resolveFor: resolveForTemplate,
  } = useEmailTemplateResolution(orgData?.emailTemplates, templateContext);
  const fallbackSubject = formNumber ?? '';
  const subjectDefault = initialSubject || fallbackSubject;
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | undefined>(initialTemplate?.id);

  const handleTemplateChange = useCallback((id: string) => {
    const resolved = resolveForTemplate(id);
    if (!resolved) return;
    setSelectedTemplateId(id);
    form.setFieldsValue({
      subject: resolved.subject || fallbackSubject,
      message: resolved.message,
      signature: resolved.signature,
    });
  }, [resolveForTemplate, fallbackSubject, form]);

  const formDocIds = useMemo(() => {
    const ids = new Set<string>();

    const entityRefs = formVersion?.data?.entityReferences;
    if (entityRefs) {
      for (const ref of Object.values(entityRefs)) {
        if (ref.entityRecordId) ids.add(ref.entityRecordId);
      }
    }

    const fields = formVersion?.data?.fields;
    if (fields) {
      for (const field of Object.values(fields)) {
        const trees = (field as { value?: { trees?: TreeLikeNode[] } }).value?.trees;
        if (Array.isArray(trees)) collectTreeNodeIds(trees, ids);
      }
    }

    if (pageForm?.data?.assignedTo) ids.add(pageForm.data.assignedTo);

    const subscribers = pageForm?.data?.notificationsSubscribers;
    if (subscribers) {
      for (const sub of subscribers) ids.add(sub);
    }

    return Array.from(ids);
  }, [
    formVersion?.data?.entityReferences,
    formVersion?.data?.fields,
    pageForm?.data?.assignedTo,
    pageForm?.data?.notificationsSubscribers,
  ]);

  const { includedOptions: formContactOptions, loading: formContactsLoading } = useContactSearch({
    kind: 'email',
    includeDocIds: formDocIds,
  });

  const { shareMultipleVersions, uploadFiles } = useAttachments({
    value: formAttachments,
    onError: (error) => messageApi.error(error.message),
    onSuccess: (message) => messageApi.success(message),
  });

  const { ensureFilesShared } = useFileSharing({
    shareMultipleVersions,
    onError: (msg: string) => messageApi.error(msg),
    onLoading: (msg: string) => messageApi.loading(msg),
  });

  const selectedPdfView: VisibilityTypeId = Form.useWatch('pdfVisibilityMode', form) ?? defaultPdfExportMode;
  const includePdfChecked: boolean = Form.useWatch('includePdf', form) !== false;

  const { generatePdfAttachment, isLoadingParams, paramsError, portalNode, portalReady } = usePdfAttachment({ formNumber, open: true, visibilityMode: selectedPdfView });
  const {
    generateXlsxAttachment,
    tableOptions: xlsxTableOptions,
    visibilityGate: xlsxVisibilityGate,
  } = useXlsxAttachment({ visibilityModeOverride: selectedPdfView });

  const handleSend = async () => {
    setLoading(true);
    try {
      const values = await form.validateFields();
      const includePdf: boolean = form.getFieldValue('includePdf') !== false;
      const includeXlsx: boolean = form.getFieldValue('includeXlsx') === true;
      const selectedXlsxTableKeys: string[] | undefined = form.getFieldValue('xlsxTableKeys');

      if (includePdf && !portalReady) {
        messageApi.error('PDF export portal is not ready.');
        setLoading(false);
        return;
      }
      if (includePdf && isLoadingParams) {
        messageApi.warning('Organization details are still loading, please wait a moment and try again.');
        setLoading(false);
        return;
      }
      if (includePdf && paramsError) {
        messageApi.error(`Error with PDF parameters: ${paramsError}`);
        setLoading(false);
        return;
      }
      if (includeXlsx && xlsxVisibilityGate) {
        messageApi[xlsxVisibilityGate.severity](xlsxVisibilityGate.message);
        setLoading(false);
        return;
      }

      const toList = (values.to ?? []).filter((e: string) => typeof e === 'string' && e.trim().length > 0).map((e: string) => e.trim());
      const ccList = (values.cc ?? []).filter((e: string) => typeof e === 'string' && e.trim().length > 0).map((e: string) => e.trim());
      const bccList = (values.bcc ?? []).filter((e: string) => typeof e === 'string' && e.trim().length > 0).map((e: string) => e.trim());

      if (toList.length === 0) {
        messageApi.error('Please enter at least one valid recipient email address');
        setLoading(false);
        return;
      }

      const finalFileLinks = await ensureFilesShared(selectedFileLinks);
      setSelectedFileLinks(finalFileLinks);

      const attachments: Array<{ filename: string; content: string; contentType: string }> = [];

      if (includePdf) {
        const pdfAttachment = await generatePdfAttachment({
          formNumber,
          formTitle,
        });
        attachments.push(pdfAttachment);
      }

      if (includeXlsx && xlsxTableOptions.length > 0) {
        try {
          const sanitize = (str: string) => str.replace(/[^a-zA-Z0-9-_]/g, '-');
          const filenameBase = formLabel ? sanitize(formLabel) : 'export';
          const xlsxAttachment = await generateXlsxAttachment({
            selectedKeys: selectedXlsxTableKeys,
            formNumber: formLabel,
            filenameBase,
          });
          attachments.push(xlsxAttachment);
        } catch (error) {
          if (error instanceof Error) messageApi.error(error.message);
          setLoading(false);
          return;
        }
      }

      const { text: emailText, html: emailHtml } = prepareEmailBody({
        message: values.message,
        signature: values.signature,
        fileLinks: finalFileLinks,
      });

      const replyTo = emailSenderMode === 'system'
        ? defaultSystemFromEmail
        : emailSenderMode === 'user' ? user?.email : emailSenderMode;

      /**
       * `cc`, `bcc` and `replyTo` are spread conditionally, never spelled: the
       * callable client encodes a property whose value is `undefined` as `null` and
       * only drops an absent key, so a spelled optional recipient reached
       * `sendEmail` as `null` (SYD-2445).
       */
      await sendEmail({
        to: toList,
        ...(ccList.length > 0 ? { cc: ccList } : {}),
        ...(bccList.length > 0 ? { bcc: bccList } : {}),
        subject: values.subject,
        text: emailText,
        html: emailHtml,
        formId,
        attachments,
        ...(replyTo ? { replyTo } : {}),
      });

      const xlsxIncludedInEmail = includeXlsx && xlsxTableOptions.length > 0;

      if (formVersion?.data?.docId) {
        const commLogFieldId = 'communicationLog';
        const logEntryId = crypto.randomUUID();
        const artifactAttachments = await (async (): Promise<
          ReturnType<typeof artifactAttachmentsFromUploadResults> | undefined
        > => {
          if (attachments.length === 0) return undefined;
          try {
            const files: RcFile[] = attachments.map((a) => {
              const file = emailAttachmentBlobToFile(a);
              const rcFile = file as RcFile;
              rcFile.uid = crypto.randomUUID();
              return rcFile;
            });
            const uploadResults = await uploadFiles(files, logEntryId, { suppressSuccessToast: true });
            return uploadResults.length > 0
              ? artifactAttachmentsFromUploadResults(uploadResults)
              : undefined;
          } catch (error) {
            console.warn('Failed to store outbound email attachments for communication log:', error);
            return undefined;
          }
        })();
        const [storedBody] = await uploadFiles(
          [new File([emailHtml], `email-${logEntryId}.html`, { type: 'text/html' })],
          logEntryId,
          { suppressSuccessToast: true },
        );
        const logEntry = createEmailLogEntry({
          to: toList,
          cc: ccList.length > 0 ? ccList : undefined,
          bcc: bccList.length > 0 ? bccList : undefined,
          subject: values.subject,
          message: values.message,
          userEmail: user?.email ?? undefined,
          includePdf,
          includeXlsxInEmail: xlsxIncludedInEmail,
          selectedXlsxTableKeys,
          formAttachments,
          splitQuery,
          id: logEntryId,
          artifactAttachments,
          bodyAttachment: storedBody?.versionData.storagePath
            ? { fileUid: storedBody.fileUid, versionId: storedBody.versionId, storagePath: storedBody.versionData.storagePath }
            : undefined,
          outboundAttachmentLabels: attachments.map((a) => a.filename),
        });
        try {
          await CommunicationLogBundles.appendRecord({
            formId,
            formVersionId: formVersion.data.docId,
            fieldId: commLogFieldId,
            record: logEntry,
          });
        } catch (error) {
          console.warn('Failed to add email to communication log:', error);
          messageApi.warning('Email sent successfully, but could not be added to communication log');
        }
      }

      messageApi.success('Email sent successfully');
      onCancel();
      form.resetFields();
      setSelectedFileLinks([]);
    } catch (error) {
      if (error instanceof Error) {
        messageApi.error(`Failed to send email: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    setSelectedFileLinks([]);
    setEmailSenderModeOverride(undefined);
    onCancel();
  };

  const defaultCc = useMemo(() => (user?.email ? [user.email] : []), [user?.email]);

  return (
    <Modal
      title="Send via Email"
      open
      onCancel={handleClose}
      destroyOnHidden
      width={600}
      footer={
        <FlexBox>
          <Button onClick={handleClose} aria-label="send-email-cancel">
            Cancel
          </Button>
          <Button
            type="primary"
            loading={loading || isLoadingParams || (includePdfChecked && !portalReady)}
            onClick={() => void handleSend()}
            aria-label="send-email-send"
          >
            Send
          </Button>
        </FlexBox>
      }
    >
      <FlexCol stretch>
        <Segmented
          block
          options={[
            { label: 'Form Contacts', value: 'form' },
            { label: 'All Contacts', value: 'all' },
          ]}
          value={contactScope}
          onChange={(value) => setContactScope(value as 'form' | 'all')}
          aria-label="send-email-contact-scope"
          style={{ width: '100%' }}
        />
        <FlexCol noGap>
          <Text>
            Reply to
          </Text>
          {emailSenderPolicy?.allowUserChoice === false ? (
            <Text>
              {replyToOptions.find((option) => option.value === emailSenderMode)?.label}
            </Text>
          ) : (
            <Select
              options={replyToOptions}
              value={emailSenderMode}
              onChange={(value) => setEmailSenderModeOverride(value)}
              aria-label="send-email-sender-mode"
              variant="filled"
              style={{ width: '100%' }}
            />
          )}
          {emailSenderMode === 'user' && !user?.email && (
            <Text type="warning">Your account has no email address configured</Text>
          )}
        </FlexCol>
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          style={{ width: '100%' }}
          initialValues={{
            pdfVisibilityMode: defaultPdfExportMode,
            to: initialTo ?? [],
            cc: defaultCc,
            bcc: [],
            subject: subjectDefault,
            message: initialMessage,
            signature: initialSignature,
            includePdf: true,
            includeXlsx: true,
          }}
        >
          <FlexBox style={{ alignItems: 'flex-end', gap: token.paddingXS, marginBottom: token.marginSM }}>
            <Form.Item name="pdfVisibilityMode" label="Export view" style={{ flex: 1, minWidth: 0, marginBottom: 0 }}>
              <Select
                options={pdfVisibilitySelectOptions}
                placeholder="Generic PDF export view"
                disabled={loading || pdfVisibilitySelectOptions.length === 0}
                aria-label="send-email-pdf-visibility"
                variant="filled"
              />
            </Form.Item>
            {includePdfChecked && (
              <Button
                type="default"
                onClick={() => setIsPdfPreviewOpen(true)}
                disabled={loading || pdfVisibilitySelectOptions.length === 0}
                aria-label="send-email-preview-pdf"
              >
                Preview PDF
              </Button>
            )}
          </FlexBox>
          {isPdfPreviewOpen && (
            <FormExportModal
              onCancel={() => setIsPdfPreviewOpen(false)}
              initialVisibilityMode={form.getFieldValue('pdfVisibilityMode') ?? defaultPdfExportMode}
            />
          )}
          <Form.Item
            name="to"
            label="To"
            rules={[
              { required: true, message: 'Please enter at least one recipient' },
              { validator: emailValidator },
            ]}
          >
            <RecipientSelect
              placeholder="Recipients"
              ariaLabel="send-email-to-select"
              scope={contactScope}
              formOptions={formContactOptions}
              formOptionsLoading={formContactsLoading}
              disabled={loading}
              maxTagCount={1}
              maxTagPlaceholder={(omitted) => `+${omitted.length}`}
            />
          </Form.Item>
          <Form.Item name="cc" label="Cc" rules={[{ validator: emailValidator }]}>
            <RecipientSelect
              placeholder="Cc"
              ariaLabel="send-email-cc-select"
              scope={contactScope}
              formOptions={formContactOptions}
              formOptionsLoading={formContactsLoading}
              disabled={loading}
              maxTagCount={1}
              maxTagPlaceholder={(omitted) => `+${omitted.length}`}
            />
          </Form.Item>
          <Form.Item name="bcc" label="Bcc" rules={[{ validator: emailValidator }]}>
            <RecipientSelect
              placeholder="Bcc"
              ariaLabel="send-email-bcc-select"
              scope={contactScope}
              formOptions={formContactOptions}
              formOptionsLoading={formContactsLoading}
              disabled={loading}
              maxTagCount={1}
              maxTagPlaceholder={(omitted) => `+${omitted.length}`}
            />
          </Form.Item>
          {templateEntries.length >= 1 && (
            <Form.Item label="Email template">
              <Select
                options={templateSelectOptions}
                value={selectedTemplateId}
                onChange={handleTemplateChange}
                aria-label="send-email-template"
                variant="filled"
              />
            </Form.Item>
          )}
          <Form.Item
            name="subject"
            label="Subject"
            rules={[{ required: true, message: 'Please enter a subject' }]}
          >
            <Input aria-label="send-email-subject" variant="filled" />
          </Form.Item>
          <Form.Item
            name="message"
            label="Message"
            rules={[{ max: MAX_LENGTHS.longText, message: `Message cannot exceed ${MAX_LENGTHS.longText} characters` }]}
          >
            <TextArea
              autoSize={{ minRows: 4, maxRows: 8 }}
              placeholder="Optional message to include with the form"
              aria-label="send-email-message"
              maxLength={MAX_LENGTHS.longText}
              showCount
              variant="filled"
            />
          </Form.Item>
          <Form.Item
            name="signature"
            label="Signature"
            rules={[{ max: MAX_LENGTHS.longText, message: `Signature cannot exceed ${MAX_LENGTHS.longText} characters` }]}
          >
            <TextArea
              autoSize={{ minRows: 2, maxRows: 6 }}
              placeholder="Optional signature"
              aria-label="send-email-signature"
              maxLength={MAX_LENGTHS.longText}
              showCount
              variant="filled"
            />
          </Form.Item>
          <Form.Item name="includePdf" valuePropName="checked" style={{ margin: 0 }}>
            <Checkbox disabled={loading} aria-label="send-email-include-pdf">
              Include PDF attachment
            </Checkbox>
          </Form.Item>
          <Form.Item name="includeXlsx" valuePropName="checked">
            <Checkbox
              disabled={loading || xlsxTableOptions.length === 0}
              aria-label="send-email-include-xlsx"
            >
              Include XLSX (table) attachment
            </Checkbox>
          </Form.Item>
          <Form.Item shouldUpdate noStyle>
            {({ getFieldValue }) =>
              getFieldValue('includeXlsx') && xlsxTableOptions.length > 1 ? (
                <Form.Item name="xlsxTableKeys" label="Tables to include">
                  <Select
                    mode="multiple"
                    placeholder="All tables (default)"
                    disabled={loading || xlsxTableOptions.length === 0}
                    aria-label="send-email-xlsx-tables"
                    options={xlsxTableOptions.map((t) => ({
                      value: t.key,
                      label: (
                        <Text>
                          {t.label} <Text type="secondary">{t.rowCount} rows</Text>
                        </Text>
                      ),
                    }))}
                  />
                </Form.Item>
              ) : null
            }
          </Form.Item>
          <FileLinksSelector
            selectedFiles={selectedFileLinks}
            onSelectedFilesChange={setSelectedFileLinks}
            availableAttachments={formAttachments}
            disabled={loading || isLoadingParams}
          />
        </Form>
      </FlexCol>
      {portalNode}
    </Modal>
  );
};

export default FormEmailModal;
